import { Fragment, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useCategoriesByBrandsCatalogStore } from '@/stores/feed/categoriesByBrandsCatalogStore'
import { useSearchStore } from '@/stores/feed/searchStore'
import { useTreeTilesStore } from '@/stores/feed/treeTilesStore'
import { useUserInfoStore } from '@/stores/user/userInfoStore'
import type { CategoryInterface, NomenclatureGroupInterface } from '@/types'
import { isNomenclatureEditor } from '@/utils/auth/userRoles'
import getPathForSelectedTreeNode from '@/utils/tree/nodes/getPathForSelectedTreeNode'

import cn from './FeedSearchTerm.module.scss'

function FeedSearchTerm() {
  const [pathToSelectedCategory, setPathToSelectedCategory] = useState<
    CategoryInterface[]
  >([])

  const [pathToSelectedNomenclatureGroup, setPathToSelectedNomenclatureGroup] =
    useState<NomenclatureGroupInterface[]>([])

  const {
    searchTerm,
    currentlySearchableCategories,
    currentlySearchableNomenclatureGroupId,
    currentlySearchableBrands,
    availableFilters,
    serverChosenFilters,
  } = useSearchStore((state) => state)

  const { nomenclatureGroupsTree, categoriesTree } = useTreeTilesStore(
    (state) => state
  )

  const { currentBrand } = useCategoriesByBrandsCatalogStore((state) => state)

  const selectedOrganisation = useUserInfoStore(
    (state) => state.selectedOrganisation
  )
  const isEditor = useUserInfoStore((state) =>
    isNomenclatureEditor(state.tokenRoles)
  )
  const navigate = useNavigate()

  const displayBrand = useMemo(() => {
    const brand = currentlySearchableBrands[0]
    if (!brand) return null
    if (brand.description) return brand

    const fromServer = serverChosenFilters?.brands?.find(
      (b) => b.id === brand.id
    )
    if (fromServer?.description) return fromServer

    const fromAvailable = availableFilters?.brands?.find(
      (b) => b.id === brand.id
    )
    if (fromAvailable?.description) return fromAvailable

    return brand
  }, [currentlySearchableBrands, serverChosenFilters, availableFilters])

  const getCategoryDisplayName = (cat: CategoryInterface) => {
    if (cat.category.description) return cat.category.description

    const fromServer = serverChosenFilters?.categories?.find(
      (c) => c.id === cat.category.id
    )
    return fromServer?.description || '...'
  }

  const getGroupDisplayName = (group: NomenclatureGroupInterface) => {
    if (group.nomenclatureGroup.description)
      return group.nomenclatureGroup.description

    const fromServer = serverChosenFilters?.parents?.find(
      (p) => p.id === group.nomenclatureGroup.id
    )
    return fromServer?.description || '...'
  }

  useEffect(() => {
    if (categoriesTree.length && currentlySearchableCategories.length === 1) {
      const path = getPathForSelectedTreeNode(
        categoriesTree,
        currentlySearchableCategories[0].id,
        []
      ) as CategoryInterface[]
      if (path) setPathToSelectedCategory(path)
    } else {
      setPathToSelectedCategory([])
    }

    if (
      nomenclatureGroupsTree.length &&
      currentlySearchableNomenclatureGroupId
    ) {
      const path = getPathForSelectedTreeNode(
        nomenclatureGroupsTree,
        currentlySearchableNomenclatureGroupId,
        []
      ) as NomenclatureGroupInterface[]
      if (path) setPathToSelectedNomenclatureGroup(path)
    } else {
      setPathToSelectedNomenclatureGroup([])
    }
  }, [
    categoriesTree,
    nomenclatureGroupsTree,
    currentlySearchableCategories,
    currentlySearchableNomenclatureGroupId,
  ])

  const handleRedirectToSelectedCategory = (pathItem: CategoryInterface) => {
    const brandFromSearch = currentlySearchableBrands[0]
    const hasBrand =
      currentlySearchableBrands.length === 1 ||
      Object.keys(currentBrand).length > 0

    const params = new URLSearchParams()
    params.set('categoryId', pathItem.category.id)

    if (currentlySearchableBrands.length === 1) {
      params.set('brandId', brandFromSearch.id)
    } else if (currentBrand?.id) {
      params.set('brandId', currentBrand.id)
    }

    const destination = isEditor
      ? hasBrand
        ? '/categories-by-brands-catalog'
        : '/categories-catalog'
      : hasBrand
        ? '/categories-by-brands-catalog'
        : '/'

    navigate(
      {
        pathname: destination,
        search: params.toString(),
      },
      {
        state: { isJump: true },
      }
    )
  }

  const handleRedirectToSelectedNomenclatureGroup = (
    pathItem: NomenclatureGroupInterface
  ) => {
    const params = new URLSearchParams()
    params.set('groupId', pathItem.nomenclatureGroup.id)

    navigate(
      {
        pathname: '/nomenclature-groups-catalog',
        search: params.toString(),
      },
      {
        state: { isJump: true },
      }
    )
  }

  const handleRedirectToBrand = () => {
    if (displayBrand) {
      navigate(
        {
          pathname: '/categories-by-brands-catalog',
          search: `?brandId=${displayBrand.id}`,
        },
        {
          state: { isJump: true },
        }
      )
    }
  }

  return (
    <div
      className={[
        cn.feedSearchTerm,
        Object.keys(selectedOrganisation).length > 0
          ? cn.organisationSelected
          : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {searchTerm.length > 0 &&
        !currentlySearchableBrands.length &&
        !currentlySearchableCategories.length &&
        !currentlySearchableNomenclatureGroupId && (
          <span
            className={
              Object.keys(selectedOrganisation).length
                ? [cn.searchTerm, cn.organisationSelected].join(' ')
                : cn.searchTerm
            }
          >
            {searchTerm}
          </span>
        )}
      {searchTerm.length > 0 &&
        [
          currentlySearchableBrands.length > 1,
          currentlySearchableCategories.length > 1,
        ].some((value) => value) && (
          <span
            className={
              Object.keys(selectedOrganisation).length
                ? [cn.searchTerm, cn.organisationSelected].join(' ')
                : cn.searchTerm
            }
          >
            {searchTerm}
          </span>
        )}

      {/* ОДИН БРЕНД (БЕЗ КАТЕГОРИЙ) */}
      {currentlySearchableBrands.length === 1 &&
        !currentlySearchableCategories.length &&
        displayBrand && (
          <div
            className={cn.descriptionWithSeparator}
            onClick={handleRedirectToBrand}
          >
            <span className={cn.description}>
              {displayBrand.description || '...'}
            </span>
            {searchTerm.length > 0 && (
              <span className={cn.searchTerm}>({searchTerm})</span>
            )}
          </div>
        )}

      {/* ОДИН БРЕНД + ПУТЬ КАТЕГОРИЙ */}
      {currentlySearchableBrands.length === 1 &&
        currentlySearchableCategories.length === 1 &&
        displayBrand && (
          <Fragment key={`brand-${displayBrand.id}-category-path`}>
            <div
              className={cn.descriptionWithSeparator}
              onClick={handleRedirectToBrand}
            >
              <span className={cn.description}>
                {displayBrand.description || '...'}
              </span>
              <span className={cn.separator}>{'>'}</span>
            </div>
            {pathToSelectedCategory.map((pathToSelectedCategoryItem, index) => {
              const isLast =
                pathToSelectedCategory[pathToSelectedCategory.length - 1]
                  .category.id === pathToSelectedCategoryItem.category.id

              // Так как бренд уже занял 16px, категории начинаем с 18px
              const currentFontSize = 18 + index * 2

              if (!isLast) {
                return (
                  <div
                    className={cn.descriptionWithSeparator}
                    onClick={() =>
                      handleRedirectToSelectedCategory(
                        pathToSelectedCategoryItem
                      )
                    }
                    key={`brand-category-${pathToSelectedCategoryItem.category.id}`}
                    style={{ '--crumb-font-size': `${currentFontSize}px` }}
                  >
                    <span className={cn.description}>
                      {getCategoryDisplayName(pathToSelectedCategoryItem)}
                    </span>
                    <span className={cn.separator}>{'>'}</span>
                  </div>
                )
              } else {
                return (
                  <div
                    className={cn.lastInPathDescriptionWithSearchTerm}
                    key={`brand-category-last-${pathToSelectedCategoryItem.category.id}`}
                    style={{ '--crumb-font-size': `${currentFontSize}px` }}
                  >
                    <span className={cn.lastInPathDescription}>
                      {getCategoryDisplayName(pathToSelectedCategoryItem)}
                    </span>
                    {searchTerm.length > 0 && (
                      <span className={cn.searchTerm}>({searchTerm})</span>
                    )}
                  </div>
                )
              }
            })}
          </Fragment>
        )}

      {/* ТОЛЬКО ПУТЬ КАТЕГОРИЙ (БЕЗ БРЕНДА) */}
      {!currentlySearchableBrands.length &&
        currentlySearchableCategories.length === 1 &&
        pathToSelectedCategory.map((pathToSelectedCategoryItem, index) => {
          const isLast =
            pathToSelectedCategory[pathToSelectedCategory.length - 1].category
              .id === pathToSelectedCategoryItem.category.id

          const currentFontSize = 16 + index * 2

          if (!isLast) {
            return (
              <div
                className={cn.descriptionWithSeparator}
                onClick={() =>
                  handleRedirectToSelectedCategory(pathToSelectedCategoryItem)
                }
                key={`category-${pathToSelectedCategoryItem.category.id}`}
                style={{ '--crumb-font-size': `${currentFontSize}px` }}
              >
                <span className={cn.description}>
                  {getCategoryDisplayName(pathToSelectedCategoryItem)}
                </span>
                <span className={cn.separator}>{'>'}</span>
              </div>
            )
          } else {
            return (
              <div
                className={cn.lastInPathDescriptionWithSearchTerm}
                key={`category-last-${pathToSelectedCategoryItem.category.id}`}
                style={{ '--crumb-font-size': `${currentFontSize}px` }}
              >
                <span className={cn.lastInPathDescription}>
                  {getCategoryDisplayName(pathToSelectedCategoryItem)}
                </span>
                {searchTerm && (
                  <span className={cn.searchTerm}>({searchTerm})</span>
                )}
              </div>
            )
          }
        })}

      {/* ПУТЬ НОМЕНКЛАТУРНЫХ ГРУПП (ДЛЯ РЕДАКТОРА) */}
      {currentlySearchableNomenclatureGroupId &&
        pathToSelectedNomenclatureGroup.map(
          (pathToSelectedNomenclatureGroupItem, index) => {
            const isLast =
              pathToSelectedNomenclatureGroup[
                pathToSelectedNomenclatureGroup.length - 1
              ].nomenclatureGroup.id ===
              pathToSelectedNomenclatureGroupItem.nomenclatureGroup.id

            const currentFontSize = 16 + index * 2

            if (!isLast) {
              return (
                <div
                  className={cn.descriptionWithSeparator}
                  onClick={() =>
                    handleRedirectToSelectedNomenclatureGroup(
                      pathToSelectedNomenclatureGroupItem
                    )
                  }
                  key={`nomenclature-${pathToSelectedNomenclatureGroupItem.nomenclatureGroup.id}`}
                  style={{ '--crumb-font-size': `${currentFontSize}px` }}
                >
                  <span className={cn.description}>
                    {getGroupDisplayName(pathToSelectedNomenclatureGroupItem)}
                  </span>
                  <span className={cn.separator}>{'>'}</span>
                </div>
              )
            } else {
              return (
                <div
                  className={cn.lastInPathDescriptionWithSearchTerm}
                  key={`nom-last-${pathToSelectedNomenclatureGroupItem.nomenclatureGroup.id}`}
                  style={{ '--crumb-font-size': `${currentFontSize}px` }}
                >
                  <span className={cn.lastInPathDescription}>
                    {getGroupDisplayName(pathToSelectedNomenclatureGroupItem)}
                  </span>
                  {searchTerm && (
                    <span className={cn.searchTerm}>({searchTerm})</span>
                  )}
                </div>
              )
            }
          }
        )}
    </div>
  )
}

export default FeedSearchTerm
